import React from 'react';
import { calculateTotalWeight, calculateTotalCases } from '../../utils/formatHelpers';
import { dateToJulian } from '../../utils/lotNumberHelpers';

/**
 * PalletModal - Modal for creating, editing, and viewing pallets
 */
const PalletModal = ({
  isOpen,
  mode, // 'create', 'edit', 'view'
  pallet,
  palletItems,
  // Form state
  customers,
  allItems,
  selectedCustomerId,
  onCustomerChange,
  customerSearch,
  onCustomerSearchChange,
  showCustomerDropdown,
  onShowCustomerDropdown,
  filteredCustomers,
  onSelectCustomer,
  onCustomerFocus,
  onCustomerBlur,
  // Item management
  itemSearch,
  onItemSearchChange,
  showItemDropdown,
  onShowItemDropdown,
  filteredItems,
  onSelectItem,
  selectedItemId,
  quantity,
  onQuantityChange,
  lotNumber,
  onLotNumberChange,
  lotDate,
  onLotDateChange,
  onAddItem,
  onRemoveItem,
  onUpdateQuantity,
  editingItemIndex,
  onEditItem,
  onCancelEditItem,
  // Label/options
  labelText,
  onLabelTextChange,
  labelColor,
  onLabelColorChange,
  isFull,
  onIsFullChange,
  notes,
  onNotesChange,
  // Actions
  onSave,
  onDelete,
  onEdit,
  onClose,
  saving,
  showDeleteConfirm,
  onShowDeleteConfirm,
  onCancelDelete,
  onConfirmDelete,
  // Customer change confirmation
  pendingShipments,
  showCustomerChangeConfirm,
  onCancelCustomerChange,
  onConfirmCustomerChange,
  // Wheel picker
  wheels,
  onOpenWheelPicker,
  // Scanning
  onScanItem,
  // PDF
  onGenerateLabel,
  onGeneratePrintNumber,
  // Image
  imagePreview,
  uploadingImage,
  // Wheel restore
  restoreWheel,
  onRestoreWheelChange
}) => {
  if (!isOpen) return null;

  const totalWeight = calculateTotalWeight(palletItems);
  const totalCases = calculateTotalCases(palletItems);

  return (
    <div className="modal-overlay">
      <div className="modal pallet-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h2>
              {mode === 'create' && 'Create New Pallet'}
              {mode === 'edit' && `Edit Pallet ${pallet?.pallet_number}`}
              {mode === 'view' && `Pallet ${pallet?.pallet_number}`}
            </h2>
            {mode === 'view' && pallet?.is_full === 1 && (
              <span className="full-badge-header">FULL</span>
            )}
            {pallet?.label_text && (
              <span
                className="label-badge-header"
                style={{ backgroundColor: pallet.label_color || '#757575' }}
              >
                {pallet.label_text}
              </span>
            )}
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body invoice-style">
          {/* ===== CREATE/EDIT MODE ===== */}
          {mode !== 'view' && (
            <div className="invoice-form">
              {/* Customer Row */}
              <div className="invoice-header-row">
                <div className="invoice-customer">
                  <label>Customer</label>
                  <div className="invoice-search-wrap">
                    <input
                      type="text"
                      value={customerSearch}
                      onChange={(e) => {
                        onCustomerSearchChange(e.target.value);
                        onCustomerChange('');
                        onShowCustomerDropdown(true);
                      }}
                      onFocus={onCustomerFocus}
                      onBlur={onCustomerBlur}
                      placeholder="Search customer..."
                    />
                    <span className={`invoice-customer-arrow${showCustomerDropdown ? ' open' : ''}`}>▼</span>
                    {showCustomerDropdown && filteredCustomers.length > 0 && (
                      <div className="invoice-dropdown">
                        {filteredCustomers.map(customer => (
                          <div
                            key={customer.id}
                            className="invoice-dropdown-item"
                            onClick={() => onSelectCustomer(customer)}
                          >
                            <strong>{customer.name}</strong>
                            <span>{customer.city}, {customer.state}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {wheels.length > 0 && (
                  <button type="button" className="invoice-wheel-btn" onClick={onOpenWheelPicker}>
                    🛒 Pick from Wheel
                  </button>
                )}
              </div>

              {/* Label, Color & Full Badge Options */}
              <div className="pallet-options-row">
                <div className="option-group label-group">
                  <label>Label</label>
                  <input
                    type="text"
                    value={labelText}
                    onChange={(e) => onLabelTextChange(e.target.value)}
                    placeholder="e.g. Frozen, Rush..."
                    maxLength="20"
                  />
                </div>
                <div className="option-group color-group">
                  <label>Color</label>
                  <input
                    type="color"
                    value={labelColor || '#757575'}
                    onChange={(e) => onLabelColorChange(e.target.value)}
                    className="color-input"
                  />
                </div>
                <div className="option-group full-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={isFull}
                      onChange={(e) => onIsFullChange(e.target.checked)}
                    />
                    Mark as Full
                  </label>
                </div>
              </div>

              {/* Notes Field */}
              <div className="pallet-notes-row">
                <label>Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => onNotesChange(e.target.value)}
                  placeholder="Internal notes (not shown on labels)..."
                  rows="2"
                />
              </div>

              {/* Line Items Table - Desktop */}
              <div className="invoice-table-wrap">
                <table className="invoice-table">
                  <thead>
                    <tr>
                      <th className="col-item">Item</th>
                      <th className="col-qty">Qty</th>
                      <th className="col-lot">Lot #</th>
                      <th className="col-action"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {palletItems.map((item, index) => (
                      editingItemIndex === index ? (
                        <tr key={index} className="editing-row">
                          <td className="col-item">
                            <div className="invoice-item-search">
                              <input
                                type="text"
                                value={itemSearch}
                                onChange={(e) => {
                                  onItemSearchChange(e.target.value);
                                  onShowItemDropdown(true);
                                }}
                                onFocus={() => onShowItemDropdown(true)}
                                placeholder="Search item..."
                              />
                              <button type="button" className="invoice-scan-btn" onClick={onScanItem} title="Scan">
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                  <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
                                </svg>
                              </button>
                              {showItemDropdown && filteredItems.length > 0 && (
                                <div className="invoice-dropdown">
                                  {filteredItems.slice(0, 30).map(fi => (
                                    <div
                                      key={fi.id}
                                      className="invoice-dropdown-item"
                                      onClick={() => onSelectItem(fi)}
                                    >
                                      <strong>{fi.name}</strong>
                                      <span>{fi.part_number}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="col-qty">
                            <input
                              type="number"
                              value={quantity}
                              onChange={(e) => onQuantityChange(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                              onBlur={(e) => onQuantityChange(Math.max(1, parseInt(e.target.value) || 1))}
                              min="1"
                            />
                          </td>
                          <td className="col-lot">
                            <div className="invoice-lot-input">
                              <input
                                type="text"
                                value={lotNumber}
                                onChange={(e) => onLotNumberChange(e.target.value)}
                                placeholder="0546"
                                maxLength="4"
                              />
                              <label className="lot-date-btn">
                                📅
                                <input
                                  type="date"
                                  value={lotDate}
                                  onChange={(e) => {
                                    onLotDateChange(e.target.value);
                                    onLotNumberChange(dateToJulian(e.target.value));
                                  }}
                                />
                              </label>
                            </div>
                          </td>
                          <td className="col-action">
                            <button className="invoice-cancel-edit-btn" onClick={onCancelEditItem} title="Cancel">✕</button>
                            <button className="invoice-add-btn" onClick={onAddItem} disabled={!selectedItemId} title="Save">✓</button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={index}>
                          <td className="col-item">
                            <span className="item-part">{item.part_number}</span>
                            <span className="item-name">{item.name}</span>
                          </td>
                          <td className="col-qty">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => onUpdateQuantity(index, e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                              onBlur={(e) => onUpdateQuantity(index, Math.max(1, parseInt(e.target.value) || 1))}
                              min="1"
                            />
                          </td>
                          <td className="col-lot">{item.lot_number}</td>
                          <td className="col-action">
                            <button className="invoice-edit-btn" onClick={() => onEditItem(index)} title="Edit item">✏️</button>
                            <button className="invoice-remove-btn" onClick={() => onRemoveItem(index)}>×</button>
                          </td>
                        </tr>
                      )
                    ))}
                    {/* Add Item Row — hidden while editing an existing row */}
                    {editingItemIndex === null && (
                    <tr className="add-row">
                      <td className="col-item">
                        <div className="invoice-item-search">
                          <input
                            type="text"
                            value={itemSearch}
                            onChange={(e) => {
                              onItemSearchChange(e.target.value);
                              onShowItemDropdown(true);
                            }}
                            onFocus={() => onShowItemDropdown(true)}
                            placeholder="Search item..."
                          />
                          <button type="button" className="invoice-scan-btn" onClick={onScanItem} title="Scan">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                              <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
                            </svg>
                          </button>
                          {showItemDropdown && filteredItems.length > 0 && (
                            <div className="invoice-dropdown">
                              {filteredItems.slice(0, 30).map(item => (
                                <div
                                  key={item.id}
                                  className="invoice-dropdown-item"
                                  onClick={() => onSelectItem(item)}
                                >
                                  <strong>{item.name}</strong>
                                  <span>{item.part_number}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="col-qty">
                        <input
                          type="number"
                          value={quantity}
                          onChange={(e) => onQuantityChange(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                          onBlur={(e) => onQuantityChange(Math.max(1, parseInt(e.target.value) || 1))}
                          min="1"
                        />
                      </td>
                      <td className="col-lot">
                        <div className="invoice-lot-input">
                          <input
                            type="text"
                            value={lotNumber}
                            onChange={(e) => onLotNumberChange(e.target.value)}
                            placeholder="0546"
                            maxLength="4"
                          />
                          <label className="lot-date-btn">
                            📅
                            <input
                              type="date"
                              value={lotDate}
                              onChange={(e) => {
                                onLotDateChange(e.target.value);
                                onLotNumberChange(dateToJulian(e.target.value));
                              }}
                            />
                          </label>
                        </div>
                      </td>
                      <td className="col-action">
                        <button
                          className="invoice-add-btn"
                          onClick={onAddItem}
                          disabled={!selectedItemId}
                        >
                          +
                        </button>
                      </td>
                    </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Form View */}
              <div className="mobile-items-form">
                {palletItems.map((item, index) => (
                  editingItemIndex === index ? (
                    <div key={index} className="mobile-item-card editing-card">
                      <div className="form-row">
                        <label>Item</label>
                        <div className="invoice-item-search">
                          <input
                            type="text"
                            value={itemSearch}
                            onChange={(e) => {
                              onItemSearchChange(e.target.value);
                              onShowItemDropdown(true);
                            }}
                            onFocus={() => onShowItemDropdown(true)}
                            placeholder="Search item..."
                          />
                          <button type="button" className="invoice-scan-btn" onClick={onScanItem} title="Scan">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                              <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
                            </svg>
                          </button>
                          {showItemDropdown && filteredItems.length > 0 && (
                            <div className="invoice-dropdown">
                              {filteredItems.slice(0, 10).map(fi => (
                                <div
                                  key={fi.id}
                                  className="invoice-dropdown-item"
                                  onClick={() => onSelectItem(fi)}
                                >
                                  <strong>{fi.name}</strong>
                                  <span>{fi.part_number}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="row-flex">
                        <div className="form-row">
                          <label>Qty</label>
                          <input
                            type="number"
                            value={quantity}
                            onChange={(e) => onQuantityChange(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                            min="1"
                          />
                        </div>
                        <div className="form-row">
                          <label>Lot #</label>
                          <input
                            type="text"
                            value={lotNumber}
                            onChange={(e) => onLotNumberChange(e.target.value)}
                            placeholder="0546"
                            maxLength="4"
                          />
                        </div>
                      </div>
                      <div className="mobile-add-actions">
                        <button className="cancel-edit-btn" onClick={onCancelEditItem}>Cancel</button>
                        <button className="add-btn" onClick={onAddItem} disabled={!selectedItemId}>✓ Update Item</button>
                      </div>
                    </div>
                  ) : (
                    <div key={index} className="mobile-item-card">
                      <div className="item-info">
                        <span className="item-part">{item.part_number}</span>
                        <span className="item-name">{item.name}</span>
                      </div>
                      <div className="item-details">
                        <span>×{item.quantity}</span>
                        <span>{item.lot_number}</span>
                      </div>
                      <div className="mobile-item-actions">
                        <button className="mobile-edit-btn" onClick={() => onEditItem(index)} title="Edit item">✏️</button>
                        <button className="remove-btn" onClick={() => onRemoveItem(index)}>×</button>
                      </div>
                    </div>
                  )
                ))}

                {editingItemIndex === null && (
                <div className="mobile-add-form">
                  <div className="form-row">
                    <label>Item</label>
                    <div className="invoice-item-search">
                      <input
                        type="text"
                        value={itemSearch}
                        onChange={(e) => {
                          onItemSearchChange(e.target.value);
                          onShowItemDropdown(true);
                        }}
                        onFocus={() => onShowItemDropdown(true)}
                        placeholder="Search item..."
                      />
                      <button type="button" className="invoice-scan-btn" onClick={onScanItem} title="Scan">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                          <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
                        </svg>
                      </button>
                      {showItemDropdown && filteredItems.length > 0 && (
                        <div className="invoice-dropdown">
                          {filteredItems.slice(0, 10).map(item => (
                            <div
                              key={item.id}
                              className="invoice-dropdown-item"
                              onClick={() => onSelectItem(item)}
                            >
                              <strong>{item.name}</strong>
                              <span>{item.part_number}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="row-flex">
                    <div className="form-row">
                      <label>Qty</label>
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => onQuantityChange(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                        min="1"
                      />
                    </div>
                    <div className="form-row">
                      <label>Lot #</label>
                      <input
                        type="text"
                        value={lotNumber}
                        onChange={(e) => onLotNumberChange(e.target.value)}
                        placeholder="0546"
                        maxLength="4"
                      />
                    </div>
                  </div>
                  <div className="mobile-add-actions">
                    <button
                      className="add-btn"
                      onClick={onAddItem}
                      disabled={!selectedItemId}
                    >
                      + Add Item
                    </button>
                  </div>
                </div>
                )}
              </div>

              {/* Totals Row */}
              <div className="invoice-totals">
                <div className="invoice-total-row">
                  <span>Items: {palletItems.length}</span>
                  <span>Cases: {totalCases}</span>
                  <span className="total-weight">Total: {totalWeight.toFixed(2)} kg</span>
                </div>
              </div>
            </div>
          )}

          {/* ===== VIEW MODE ===== */}
          {mode === 'view' && (
            <div className="view-pallet-info">
              <div className="view-info-grid">
                {pallet?.customer_name && (
                  <div className="view-info-item">
                    <label>Customer</label>
                    <span>{pallet.customer_name}</span>
                  </div>
                )}
                {pallet?.notes && (
                  <div className="view-info-item full-width">
                    <label>Notes</label>
                    <span>{pallet.notes}</span>
                  </div>
                )}
              </div>

              <table className="view-items-table">
                <thead>
                  <tr>
                    <th>Part #</th>
                    <th>Name</th>
                    <th>Lot</th>
                    <th>Qty</th>
                    <th>Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {palletItems.map((item, index) => (
                    <tr key={index}>
                      <td>{item.part_number}</td>
                      <td>{item.name}</td>
                      <td>{item.lot_number}</td>
                      <td>{item.quantity}</td>
                      <td>{parseFloat(item.subtotal_weight || 0).toFixed(2)} kg</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3"><strong>TOTAL</strong></td>
                    <td><strong>{totalCases}</strong></td>
                    <td><strong>{totalWeight.toFixed(2)} kg</strong></td>
                  </tr>
                </tfoot>
              </table>

              {pallet?.image_path && (
                <div className="view-photo">
                  <img
                    src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}/uploads/${pallet.image_path}`}
                    alt="Pallet"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {(mode === 'edit' || (mode === 'view' && pallet?.status !== 'shipped')) && (
            <button
              className="modal-btn delete"
              onClick={onShowDeleteConfirm}
            >
              Delete
            </button>
          )}
          <button className="modal-btn close" onClick={onClose}>
            {mode === 'view' ? 'Close' : 'Cancel'}
          </button>
          {palletItems.length > 0 && mode === 'view' && (
            <>
              <button
                className="modal-btn print"
                onClick={onGenerateLabel}
              >
                Print Label
              </button>
              <button
                className="modal-btn print-number"
                onClick={onGeneratePrintNumber}
              >
                Print Number
              </button>
            </>
          )}
          {mode === 'view' && pallet?.status !== 'shipped' && (
            <button
              className="modal-btn edit"
              onClick={onEdit}
            >
              Edit
            </button>
          )}
          {mode !== 'view' && (
            <button
              className="modal-btn save"
              onClick={onSave}
              disabled={saving || palletItems.length === 0}
            >
              {saving ? 'Saving...' : (mode === 'create' ? 'Create Pallet' : 'Update Pallet')}
            </button>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-dialog">
              <h3>Delete Pallet?</h3>
              <p>Are you sure you want to delete pallet "{pallet?.pallet_number}"?</p>
              {pallet?.source_wheel_id && (
                <label className="restore-wheel-option">
                  <input
                    type="checkbox"
                    checked={restoreWheel}
                    onChange={(e) => onRestoreWheelChange(e.target.checked)}
                  />
                  Restore wheel {pallet.source_wheel_number || `#${pallet.source_wheel_id}`}
                </label>
              )}
              {!pallet?.source_wheel_id && (
                <p className="warning-text">This action cannot be undone.</p>
              )}
              <div className="delete-confirm-actions">
                <button className="cancel-btn" onClick={onCancelDelete}>
                  Cancel
                </button>
                <button className="confirm-delete-btn" onClick={onConfirmDelete}>
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Customer Change Confirmation Dialog */}
        {showCustomerChangeConfirm && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-dialog">
              <h3>Change customer on a pallet that's on a shipment?</h3>
              <p>
                Pallet {pallet?.pallet_number} is on pending{' '}
                {pendingShipments.length > 1 ? 'shipments' : 'shipment'}{' '}
                <strong>{pendingShipments.map(s => s.shipment_number).join(', ')}</strong> ({pallet?.customer_name}).
              </p>
              <p className="warning-text">
                Changing its customer to <strong>{customers.find(c => c.id === parseInt(selectedCustomerId))?.name}</strong>{' '}
                will leave {pendingShipments.length > 1 ? 'those shipments' : 'that shipment'} holding pallets from more
                than one customer. The shipment's paperwork will need to be corrected.
              </p>
              <div className="delete-confirm-actions">
                <button className="cancel-btn" onClick={onCancelCustomerChange}>
                  Cancel
                </button>
                <button className="confirm-delete-btn" onClick={onConfirmCustomerChange}>
                  Yes, change customer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PalletModal;
